import { useRef, useState } from 'react'

import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import type { PendingToolCall } from '../core/engine/types'
import type { FilterPreviewAction } from '../core/tools/primitives/filter/show_filter_preview'
import { theme } from '@/Atomic/assets/theme'
import { OPERATOR_DEFINITIONS } from '@/Atomic/Organisms/FilterBuilder/config'

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.m};
  padding: ${theme.space.m} ${theme.space.l};
  background: ${theme.colors.background.default};
  border: 1px solid ${theme.colors.border.stroke};
  border-radius: ${theme.radii.md};
  margin: ${theme.space.xs} 0;
`

const SegmentName = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.text.primary};
`

const Description = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.text.primary};
`

const MatchCount = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${theme.colors.text.secondary};
`

const ConditionList = styled.ul`
  margin: 0;
  padding: 0 0 0 ${theme.space.m};
  font-size: 13px;
  color: ${theme.colors.text.primary};
  list-style: disc;
`

const ConditionItem = styled.li`
  margin: ${theme.space.xs} 0;
`

const Actions = styled.div`
  display: flex;
  gap: ${theme.space.s};
  justify-content: flex-end;
  flex-wrap: wrap;
`

const Btn = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: ${theme.space.xs} ${theme.space.m};
  border-radius: ${theme.radii.sm};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid
    ${({ variant }) =>
      variant === 'primary'
        ? theme.colors.primary.main
        : theme.colors.border.stroke};
  background: ${({ variant }) =>
    variant === 'primary' ? theme.colors.primary.main : 'transparent'};
  color: ${({ variant }) =>
    variant === 'primary' ? '#fff' : theme.colors.text.primary};
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover:not(:disabled) {
    background: ${({ variant }) =>
      variant === 'primary'
        ? theme.colors.primary.hover
        : theme.colors.background.hover};
    border-color: ${({ variant }) =>
      variant === 'primary'
        ? theme.colors.primary.hover
        : theme.colors.border.active};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

interface FilterPreviewCardProps {
  pending: Extract<PendingToolCall, { type: 'filterPreview' }>
  onSubmit: (result: FilterPreviewAction) => void
}

function formatValue(
  field: string,
  value: unknown,
  enumLabels?: Record<string, Record<string, string>>,
): string {
  if (Array.isArray(value)) {
    const labels = value.map(
      (v) => enumLabels?.[field]?.[String(v)] ?? String(v),
    )
    // Deduplicate — multiple PISA codes may share the same display label (e.g. all "active" variants)
    return [...new Set(labels)].join(', ')
  }
  if (value !== null && typeof value === 'object') {
    return JSON.stringify(value)
  }
  return enumLabels?.[field]?.[String(value ?? '')] ?? String(value ?? '')
}

function formatCondition(
  field: string,
  op: string,
  value: unknown,
  t: (key: string) => string,
  fieldLabels?: Record<string, string>,
  enumLabels?: Record<string, Record<string, string>>,
): string {
  const fieldLabel = fieldLabels?.[field] ?? field
  const valueStr = formatValue(field, value, enumLabels)
  const labelKey =
    OPERATOR_DEFINITIONS[op as keyof typeof OPERATOR_DEFINITIONS]?.labelKey
  const opLabel = labelKey ? t(labelKey) : op
  return `${fieldLabel} ${opLabel} ${valueStr}`
}

export function FilterPreviewCard({
  pending,
  onSubmit,
}: FilterPreviewCardProps) {
  const { t } = useTranslation()
  const firedRef = useRef(false)
  const [fired, setFired] = useState(false)

  function fire(action: FilterPreviewAction) {
    if (firedRef.current) return
    firedRef.current = true
    setFired(true)
    onSubmit(action)
  }

  const primaryAction = pending.primaryAction ?? 'apply'
  const primaryLabelKey =
    pending.primaryLabel ?? 'components.aiChatBot.filterPreview.apply'

  const conditions = pending.structure.or.flatMap((group, gi) =>
    group.and.map((cond, ci) => ({
      key: `${gi}-${ci}`,
      text: formatCondition(
        cond.field,
        cond.op,
        cond.value,
        t,
        pending.fieldLabels,
        pending.enumLabels,
      ),
    })),
  )

  return (
    <Card>
      {pending.segment && (
        <SegmentName>
          {t('components.aiChatBot.filterPreview.segmentName', {
            name: pending.segment.name,
          })}
        </SegmentName>
      )}
      <Description>{pending.description}</Description>
      <MatchCount>
        {pending.matchCount === null
          ? t('components.aiChatBot.filterPreview.matchCountUnknown')
          : pending.matchCount === 0
            ? t('components.aiChatBot.filterPreview.noMatches')
            : t('components.aiChatBot.filterPreview.matchCount_other', {
                count: pending.matchCount,
              })}
      </MatchCount>
      {conditions.length > 0 && (
        <ConditionList>
          {conditions.map((c) => (
            <ConditionItem key={c.key}>{c.text}</ConditionItem>
          ))}
        </ConditionList>
      )}
      <Actions>
        <Btn
          variant="secondary"
          type="button"
          onClick={() => fire({ action: 'cancel' })}
          disabled={fired}
        >
          {t('components.aiChatBot.filterPreview.cancel')}
        </Btn>
        <Btn
          variant="secondary"
          type="button"
          onClick={() => fire({ action: 'refine' })}
          disabled={fired}
        >
          {t('components.aiChatBot.filterPreview.refine')}
        </Btn>
        {/* The primary button stays enabled at a zero match count on purpose: a
            filter that matches nothing today can match records tomorrow (e.g.
            "activities next week"), so there is no reason to block applying or
            saving it. Only `fired` (already-actioned) disables the button. */}
        <Btn
          variant="primary"
          type="button"
          onClick={() => fire({ action: primaryAction })}
          disabled={fired}
        >
          {t(primaryLabelKey)}
        </Btn>
      </Actions>
    </Card>
  )
}
